import { useMutation } from '@tanstack/react-query'
import { AxiosError } from 'axios'
import { Building, ServerCrash } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'

import { Toast } from '@/common/components/Toast'
import { api, ApiError } from '@/lib/axios'
import { queryClient } from '@/lib/tanstack'
import { EditAccountForm } from '../schemas/EditAccountSchema'

type PutEditAccountProps = {
  id: string
  data: EditAccountForm
}

async function putEditAccount({ id, data }: PutEditAccountProps) {
  return api.backend.put(`/account/${id}`, data)
}

export function useEditAccount() {
  const { t } = useTranslation('sso')

  const { mutateAsync, isPending } = useMutation<
    unknown,
    AxiosError<ApiError>,
    PutEditAccountProps
  >({
    mutationKey: ['/account/id/edit'],
    mutationFn: putEditAccount,
    onSuccess: () => {
      toast(Toast, {
        data: {
          type: 'success',
          icon: Building,
          title: t('accounts.drawer_edit_account.toast_success.title'),
        },
      })
      queryClient.invalidateQueries({ queryKey: ['/account/paginate'] })
    },
    onError: (error) => {
      toast(Toast, {
        data: {
          type: 'danger',
          icon: ServerCrash,
          title: t('accounts.drawer_edit_account.toast_error.title'),
          description:
            error?.response?.data?.friendlyMessage ||
            t('accounts.drawer_edit_account.toast_error.description'),
        },
      })
    },
  })

  const handleEditAccount = async (id: string, data: EditAccountForm) => {
    return mutateAsync({ id, data })
  }

  return { handleEditAccount, isEditingAccount: isPending }
}
